/**
 * useTheme — global, persisted UI preferences.
 *
 * Two adjustable dimensions:
 *   - theme:     'light' (product default since 2026-08-16, per the design
 *                system's white/cream mandate) | 'dark' (mapped fallback)
 *   - fontScale: number multiplying the 16px root font-size (0.85–1.4)
 *
 * Persistence: localStorage keys `lw.theme` and `lw.fontScale`. An inline
 * script in index.html reads these pre-paint so the first render is already
 * correct — this composable just keeps Vue state in sync with the DOM and
 * re-writes the same keys on change.
 *
 * Singleton by design: a single reactive instance is shared across every
 * caller (Vue's module-level state pattern).
 */
import { ref, watch } from 'vue';

export type ThemeName = 'dark' | 'light';

const THEME_KEY = 'lw.theme';
const FONT_SCALE_KEY = 'lw.fontScale';

export const FONT_SCALE_MIN = 0.85;
export const FONT_SCALE_MAX = 1.4;

/** Preset steps surfaced in the settings UI. */
export const FONT_SCALE_PRESETS: { label: string; value: number }[] = [
  { label: 'S', value: 0.9 },
  { label: 'M', value: 1.0 },
  { label: 'L', value: 1.15 },
  { label: 'XL', value: 1.3 },
];

function readTheme(): ThemeName {
  if (typeof localStorage === 'undefined') return 'light';
  return localStorage.getItem(THEME_KEY) === 'dark' ? 'dark' : 'light';
}

function readFontScale(): number {
  if (typeof localStorage === 'undefined') return 1;
  const raw = parseFloat(localStorage.getItem(FONT_SCALE_KEY) ?? '');
  if (!isFinite(raw) || raw <= 0) return 1;
  return Math.min(FONT_SCALE_MAX, Math.max(FONT_SCALE_MIN, raw));
}

function applyTheme(t: ThemeName) {
  if (typeof document === 'undefined') return;
  // Light is the :root default; dark is the opt-in [data-theme] override.
  if (t === 'dark') document.documentElement.setAttribute('data-theme', 'dark');
  else document.documentElement.removeAttribute('data-theme');
}

function applyFontScale(s: number) {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty('--font-scale', String(s));
}

const theme = ref<ThemeName>(readTheme());
const fontScale = ref<number>(readFontScale());

// Re-apply on change (covers both initial mount and subsequent edits).
watch(
  theme,
  (t) => {
    applyTheme(t);
    try { localStorage.setItem(THEME_KEY, t); } catch { /* storage blocked */ }
  },
  { immediate: true },
);

watch(
  fontScale,
  (s) => {
    applyFontScale(s);
    try { localStorage.setItem(FONT_SCALE_KEY, String(s)); } catch { /* storage blocked */ }
  },
  { immediate: true },
);

export function useTheme() {
  function setTheme(t: ThemeName) {
    theme.value = t;
  }
  function toggleTheme() {
    theme.value = theme.value === 'dark' ? 'light' : 'dark';
  }
  function setFontScale(s: number) {
    const clamped = Math.min(FONT_SCALE_MAX, Math.max(FONT_SCALE_MIN, s));
    fontScale.value = Math.round(clamped * 100) / 100;
  }
  function bumpFontScale(delta: number) {
    setFontScale(fontScale.value + delta);
  }

  return {
    theme,
    fontScale,
    setTheme,
    toggleTheme,
    setFontScale,
    bumpFontScale,
  };
}
